import { useState } from "react";

import HandshakeIcon from "@mui/icons-material/Handshake";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";

import TextSnippetIcon from "@mui/icons-material/TextSnippet";
import {
  Box,
  IconButton,
  TableCell,
  TableRow,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";

import StatusCircle from "../../../../common/components/badge/StatusCircle";
import { useHasRoleAccess } from "../../../../common/hooks/useCheckRoleAccess";
import useLocationFromCache from "../../../../common/hooks/useLocationFromCache";
import { RoleEnum } from "../../../../common/models/enums";
import { IDeploymentHealthTableRow } from "../../hooks/useDeploymentHealthTableRows";
import ToggleTrackingDialog from "./ToggleTrackingDialog";

const MAX_DAYS_AGO = 90; // if lastAlive is more than X days ago, show > X days ago
const EPOCH_2000_MS = 946684800000; // 2000-01-01T00:00:00Z

interface ITableHeadRowProps {
  row: IDeploymentHealthTableRow;
}

const formatLastAliveAge = (epochMs: number): string => {
  const now = Date.now();
  const diffMs = Math.max(now - epochMs, 0);

  const diffMins = Math.floor(diffMs / 60000);

  // return if last alive is less than 1 minute ago
  if (diffMins === 0) return "Just now";

  // return if last alive is less than 1 hour ago
  if (diffMins < 60) return `${diffMins}m ago`;

  const diffHours = Math.floor(diffMins / 60);

  // return if last alive is less than 1 day ago
  if (diffHours < 24) return `${diffHours}h ago`;

  const diffDays = Math.floor(diffHours / 24);

  // calculate hours if last alive has non zero hours for +1 days ago
  const remainingHours = diffHours % 24;

  return diffDays >= MAX_DAYS_AGO
    ? `> ${MAX_DAYS_AGO}d ago`
    : `${diffDays}d${remainingHours > 0 ? ` ${remainingHours}h` : ""} ago`;
};

const getLastAliveLabel = (lastAliveEpochMs?: number): string => {
  // if lastAlive doesn't exist or is set to the default value of 2000-01-01T00:00:00Z, show N/A
  if (
    lastAliveEpochMs == null ||
    !Number.isFinite(lastAliveEpochMs) ||
    lastAliveEpochMs <= EPOCH_2000_MS
  ) {
    return "N/A";
  }

  return formatLastAliveAge(lastAliveEpochMs);
};

// Helper function to get the tooltip text for non-CleanConnect watchers
const getWatcherTooltipText = (assignedWatcher: string | undefined): string => {
  switch (assignedWatcher) {
    case "CUSTOMER":
      return "Customer";
    case "THIRD_PARTY":
      return "Third Party";
    default:
      return "Other";
  }
};

const DeploymentHealthTableBodyRow = ({
  row,
}: ITableHeadRowProps): JSX.Element => {
  const canEditHealth = useHasRoleAccess([
    RoleEnum.ROOT,
    RoleEnum.CC_OPS_ADMIN,
    RoleEnum.CC_OPS,
    RoleEnum.CC_SALES,
  ]);

  const theme = useTheme();

  const GREEN_ONLINE = theme.palette.primary.main;
  const RED_OFFLINE = theme.palette.error.main;
  const GREY_EXPECTED_OFFLINE = theme.palette.grey[500];

  // Helper function to calculate entity status color; reduces code duplication
  // when checking for expected offline status, else status type (green/red)
  const getEntityStatusColor = (
    isExpectedOnline: boolean | undefined,
    status: boolean
  ): string => {
    if (isExpectedOnline === false) {
      return GREY_EXPECTED_OFFLINE;
    }

    return status ? GREEN_ONLINE : RED_OFFLINE;
  };

  const { getCachedLocation } = useLocationFromCache();
  const locationCache = getCachedLocation(row.locationId);

  const [open, setOpen] = useState(!row.locationStatus);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [confirmTargetLabel, setConfirmTargetLabel] = useState<string>("");
  const [confirmTargetType, setConfirmTargetType] = useState<
    "location" | "node" | "device" | "model"
  >("location");

  const [entityId, setEntityId] = useState<string>("");
  const [locationId, setLocationId] = useState<string>("");
  const [initialIsExpectedOnline, setInitialIsExpectedOnline] = useState<
    boolean | undefined
  >(undefined);
  const [leafPk, setLeafPk] = useState<string | undefined>(undefined);
  const [leafSk, setLeafSk] = useState<string | undefined>(undefined);
  const [existingReason, setExistingReason] = useState<string | undefined>(
    undefined
  );
  const [existingAssignedWatcher, setExistingAssignedWatcher] = useState<
    string | undefined
  >(undefined);
  const [allowErrorReason, setAllowErrorReason] = useState<boolean>(false);
  const locationLabel =
    locationCache?.name ?? row.locationName ?? row.locationId;

  const FACILITY_REASONS = [
    "Onboarding",
    "Zone setup",
    "Site Power Issue",
    "Customer Network",
  ];
  const nodeAllExpectedOffline = row.nodes.every(
    n => n.isExpectedOnline === false
  );
  const deviceAllExpectedOffline = row.devices.every(
    d => d.isExpectedOnline === false
  );
  const serviceAllExpectedOffline = row.services.every(
    s => s.isExpectedOnline === false
  );
  const locationAllExpectedOffline =
    nodeAllExpectedOffline &&
    deviceAllExpectedOffline &&
    serviceAllExpectedOffline;

  const getLocationOfflineReason = (): string | undefined => {
    const childReasons = [
      ...row.nodes
        .filter(n => n.isExpectedOnline === false && !!n.reason)
        .map(n => n.reason as string),
      ...row.devices
        .filter(d => d.isExpectedOnline === false && !!d.reason)
        .map(d => d.reason as string),
      ...row.services
        .filter(s => s.isExpectedOnline === false && !!s.reason)
        .map(s => s.reason as string),
    ];

    const facilityReason = childReasons.find(r => FACILITY_REASONS.includes(r));

    return facilityReason ?? childReasons[0];
  };

  const locationReason = getLocationOfflineReason();
  const showLocationReason =
    typeof locationReason === "string" && locationReason.trim().length > 0;
  const locationDotColor = locationAllExpectedOffline
    ? theme.palette.grey[500]
    : row.locationStatus
    ? theme.palette.primary.main
    : theme.palette.error.main;

  return (
    <>
      <TableRow>
        <TableCell>
          <IconButton onClick={(): void => setOpen(!open)} size="small">
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell>
          {(() => {
            const fullyWatched =
              row.nodes.every(n => n.assignedWatcher === "CLEANCONNECT") &&
              row.devices.every(d => d.assignedWatcher === "CLEANCONNECT") &&
              row.services.every(s => s.assignedWatcher === "CLEANCONNECT");

            return (
              <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
                <StatusCircle color={locationDotColor} />
                {locationDotColor === GREY_EXPECTED_OFFLINE &&
                  showLocationReason && (
                    <Tooltip title={locationReason as string}>
                      <TextSnippetIcon
                        fontSize="small"
                        sx={{ color: theme.palette.text.secondary }}
                      />
                    </Tooltip>
                  )}
                {!fullyWatched && (
                  <Tooltip title="At least one node, device, or model is not managed by CleanConnect">
                    <HandshakeIcon
                      fontSize="inherit"
                      sx={{ color: theme.palette.info.main }}
                    />
                  </Tooltip>
                )}
              </Box>
            );
          })()}
        </TableCell>
        <TableCell>
          {canEditHealth ? (
            <Typography
              component="button"
              variant="body2"
              onClick={(): void => {
                setConfirmTargetLabel(locationLabel);

                setConfirmTargetType("location");

                setIsConfirmOpen(true);

                setEntityId(row.locationId);

                setLocationId(row.locationId);

                setInitialIsExpectedOnline(!locationAllExpectedOffline);

                setExistingReason(undefined);

                setAllowErrorReason(false);

                setExistingAssignedWatcher(undefined);
              }}
              sx={{
                border: 0,
                background: "transparent",
                p: 0,
                m: 0,
                minWidth: "auto",
                color: theme.palette.text.primary,
                cursor: "pointer",
                "&:hover": {
                  color: theme.palette.success.main,
                  backgroundColor: "transparent",
                },
              }}
            >
              {locationLabel}
            </Typography>
          ) : (
            <Typography
              variant="body2"
              sx={{
                border: 0,
                background: "transparent",
                p: 0,
                m: 0,
                minWidth: "auto",
                color: theme.palette.text.primary,
              }}
            >
              {locationLabel}
            </Typography>
          )}
          <ToggleTrackingDialog
            open={isConfirmOpen}
            targetLabel={confirmTargetLabel || locationLabel}
            entityType={confirmTargetType}
            onClose={(): void => setIsConfirmOpen(false)}
            entityId={entityId}
            locationId={locationId}
            initialIsExpectedOnline={initialIsExpectedOnline}
            leafPk={leafPk}
            leafSk={leafSk}
            existingReason={existingReason}
            existingAssignedWatcher={existingAssignedWatcher}
            allowErrorReason={allowErrorReason}
          />
        </TableCell>
        <TableCell>
          <StatusCircle
            color={
              nodeAllExpectedOffline || row.nodes.length === 0
                ? theme.palette.grey[500]
                : row.nodeStatus
                ? theme.palette.primary.main
                : theme.palette.error.main
            }
          />
        </TableCell>
        <TableCell>
          <StatusCircle
            color={
              deviceAllExpectedOffline || row.devices.length === 0
                ? theme.palette.grey[500]
                : row.deviceStatus
                ? theme.palette.primary.main
                : theme.palette.error.main
            }
          />
        </TableCell>
        <TableCell>
          <StatusCircle
            color={
              serviceAllExpectedOffline || row.services.length === 0
                ? theme.palette.grey[500]
                : row.serviceStatus
                ? theme.palette.primary.main
                : theme.palette.error.main
            }
          />
        </TableCell>
      </TableRow>

      <TableRow
        sx={{
          visibility: open ? "visible" : "collapse",
        }}
      >
        <TableCell></TableCell>
        <TableCell></TableCell>
        <TableCell></TableCell>
        <TableCell
          sx={{
            verticalAlign: "top",
          }}
        >
          {row.nodes.map((node): JSX.Element => {
            const nodeReason = node.reason ?? "";
            const showNodeReason =
              typeof nodeReason === "string" && nodeReason.trim().length > 0;
            const nodeStatusColor = getEntityStatusColor(
              node.isExpectedOnline,
              node.status
            );
            const isNotOnline = nodeStatusColor !== GREEN_ONLINE;
            const shouldShowReasonIcon = isNotOnline && showNodeReason;

            return (
              <Box
                key={node.nodeId}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "4px",
                  }}
                >
                  <StatusCircle color={nodeStatusColor} />
                  {shouldShowReasonIcon && (
                    <Tooltip title={nodeReason}>
                      <TextSnippetIcon
                        fontSize="small"
                        sx={{ color: theme.palette.text.secondary }}
                      />
                    </Tooltip>
                  )}
                  {node.assignedWatcher !== "CLEANCONNECT" && (
                    <Tooltip
                      title={getWatcherTooltipText(node.assignedWatcher)}
                    >
                      <HandshakeIcon
                        fontSize="inherit"
                        sx={{ color: theme.palette.info.main }}
                      />
                    </Tooltip>
                  )}
                </Box>
                {canEditHealth ? (
                  <Typography
                    component="button"
                    variant="body2"
                    onClick={(): void => {
                      setConfirmTargetLabel(node.nodeName);

                      setConfirmTargetType("node");

                      setEntityId(node.nodeId);

                      setLocationId(row.locationId);

                      setInitialIsExpectedOnline(
                        typeof node.isExpectedOnline === "boolean"
                          ? node.isExpectedOnline
                          : undefined
                      );

                      setLeafPk(node.leafPk);

                      setLeafSk(node.leafSk);

                      setIsConfirmOpen(true);

                      setExistingReason(
                        typeof node.reason === "string"
                          ? node.reason
                          : undefined
                      );

                      const isRed =
                        node.isExpectedOnline !== false && !node.status;

                      setAllowErrorReason(isRed);

                      setExistingAssignedWatcher(node.assignedWatcher);
                    }}
                    sx={{
                      border: 0,
                      background: "transparent",
                      p: 0,
                      m: 0,
                      minWidth: "auto",
                      color: theme.palette.text.primary,
                      cursor: "pointer",
                      "&:hover": {
                        color: theme.palette.success.main,
                        backgroundColor: "transparent",
                      },
                    }}
                  >
                    {node.nodeName}
                  </Typography>
                ) : (
                  <Typography
                    variant="body2"
                    sx={{
                      border: 0,
                      background: "transparent",
                      p: 0,
                      m: 0,
                      minWidth: "auto",
                      color: theme.palette.text.primary,
                    }}
                  >
                    {node.nodeName}
                  </Typography>
                )}
                {!node.status && node.lastAliveEpochMs && (
                  <Typography variant="caption" color="text.secondary">
                    {getLastAliveLabel(node.lastAliveEpochMs)}
                  </Typography>
                )}
              </Box>
            );
          })}
        </TableCell>
        <TableCell
          sx={{
            verticalAlign: "top",
          }}
        >
          {row.devices.map((device): JSX.Element => {
            const deviceReason = device.reason ?? "";
            const showDeviceReason =
              typeof deviceReason === "string" &&
              deviceReason.trim().length > 0;
            const deviceStatusColor = getEntityStatusColor(
              device.isExpectedOnline,
              device.status
            );
            const isNotOnline = deviceStatusColor !== GREEN_ONLINE;
            const shouldShowReasonIcon = isNotOnline && showDeviceReason;

            return (
              <Box
                key={device.deviceId}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "4px",
                  }}
                >
                  <StatusCircle color={deviceStatusColor} />
                  {shouldShowReasonIcon && (
                    <Tooltip title={deviceReason}>
                      <TextSnippetIcon
                        fontSize="small"
                        sx={{ color: theme.palette.text.secondary }}
                      />
                    </Tooltip>
                  )}
                  {device.assignedWatcher !== "CLEANCONNECT" && (
                    <Tooltip
                      title={getWatcherTooltipText(device.assignedWatcher)}
                    >
                      <HandshakeIcon
                        fontSize="inherit"
                        sx={{ color: theme.palette.info.main }}
                      />
                    </Tooltip>
                  )}
                </Box>
                {canEditHealth ? (
                  <Typography
                    component="button"
                    variant="body2"
                    onClick={(): void => {
                      setConfirmTargetLabel(device.deviceName);

                      setConfirmTargetType("device");

                      setEntityId(device.deviceId);

                      setLocationId(row.locationId);

                      setInitialIsExpectedOnline(
                        typeof device.isExpectedOnline === "boolean"
                          ? device.isExpectedOnline
                          : undefined
                      );

                      setLeafPk(device.leafPk);

                      setLeafSk(device.leafSk);

                      setIsConfirmOpen(true);

                      setExistingReason(
                        typeof device.reason === "string"
                          ? device.reason
                          : undefined
                      );

                      const isRed =
                        device.isExpectedOnline !== false && !device.status;

                      setAllowErrorReason(isRed);

                      setExistingAssignedWatcher(device.assignedWatcher);
                    }}
                    sx={{
                      border: 0,
                      background: "transparent",
                      p: 0,
                      m: 0,
                      minWidth: "auto",
                      color: theme.palette.text.primary,
                      cursor: "pointer",
                      "&:hover": {
                        color: theme.palette.success.main,
                        backgroundColor: "transparent",
                      },
                    }}
                  >
                    {device.deviceName}
                  </Typography>
                ) : (
                  <Typography
                    variant="body2"
                    sx={{
                      border: 0,
                      background: "transparent",
                      p: 0,
                      m: 0,
                      minWidth: "auto",
                      color: theme.palette.text.primary,
                    }}
                  >
                    {device.deviceName}
                  </Typography>
                )}
                {!device.status && device.lastAliveEpochMs && (
                  <Typography variant="caption" color="text.secondary">
                    {getLastAliveLabel(device.lastAliveEpochMs)}
                  </Typography>
                )}
              </Box>
            );
          })}
        </TableCell>
        <TableCell
          sx={{
            verticalAlign: "top",
          }}
        >
          {row.services.map((service): JSX.Element => {
            const serviceReason = service.reason ?? "";
            const showServiceReason =
              typeof serviceReason === "string" &&
              serviceReason.trim().length > 0;
            const serviceStatusColor = getEntityStatusColor(
              service.isExpectedOnline,
              service.status
            );
            const isNotOnline = serviceStatusColor !== GREEN_ONLINE;
            const shouldShowReasonIcon = isNotOnline && showServiceReason;

            return (
              <Box
                key={service.serviceId}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "4px",
                  }}
                >
                  <StatusCircle color={serviceStatusColor} />
                  {shouldShowReasonIcon && (
                    <Tooltip title={serviceReason}>
                      <TextSnippetIcon
                        fontSize="small"
                        sx={{ color: theme.palette.text.secondary }}
                      />
                    </Tooltip>
                  )}
                  {service.assignedWatcher !== "CLEANCONNECT" && (
                    <Tooltip
                      title={getWatcherTooltipText(service.assignedWatcher)}
                    >
                      <HandshakeIcon
                        fontSize="inherit"
                        sx={{ color: theme.palette.info.main }}
                      />
                    </Tooltip>
                  )}
                </Box>
                {canEditHealth ? (
                  <Typography
                    component="button"
                    variant="body2"
                    onClick={(): void => {
                      setConfirmTargetLabel(service.serviceLabel);

                      setConfirmTargetType("model");

                      setEntityId(service.serviceId);

                      setLocationId(row.locationId);

                      setInitialIsExpectedOnline(
                        typeof service.isExpectedOnline === "boolean"
                          ? service.isExpectedOnline
                          : undefined
                      );

                      setLeafPk(service.leafPk);

                      setLeafSk(service.leafSk);

                      setIsConfirmOpen(true);

                      setExistingReason(
                        typeof service.reason === "string"
                          ? service.reason
                          : undefined
                      );

                      const isRed =
                        service.isExpectedOnline !== false && !service.status;

                      setAllowErrorReason(isRed);

                      setExistingAssignedWatcher(service.assignedWatcher);
                    }}
                    sx={{
                      border: 0,
                      background: "transparent",
                      p: 0,
                      m: 0,
                      minWidth: "auto",
                      color: theme.palette.text.primary,
                      cursor: "pointer",
                      "&:hover": {
                        color: theme.palette.success.main,
                        backgroundColor: "transparent",
                      },
                    }}
                  >
                    {service.serviceLabel}
                  </Typography>
                ) : (
                  <Typography
                    variant="body2"
                    sx={{
                      border: 0,
                      background: "transparent",
                      p: 0,
                      m: 0,
                      minWidth: "auto",
                      color: theme.palette.text.primary,
                    }}
                  >
                    {service.serviceLabel}
                  </Typography>
                )}
                {!service.status && service.lastAliveEpochMs && (
                  <Typography variant="caption" color="text.secondary">
                    {getLastAliveLabel(service.lastAliveEpochMs)}
                  </Typography>
                )}
              </Box>
            );
          })}
        </TableCell>
      </TableRow>
    </>
  );
};

export default DeploymentHealthTableBodyRow;
